import { useState } from 'react';
import { useIsAuthenticated } from '@/stores/identity';
import { acceptDpa } from '@/lib/audit';
import { BrandedLayout } from '@/components/BrandedLayout';

/**
 * /dpa — Data Processing Addendum.
 *
 * Customer-facing legal text. Source: docs/decisions and the v2 internal
 * legal review pass (May 4, 2026). Authenticated users see an "Accept"
 * button at the bottom that records a DPA_ACCEPTED audit event with the
 * version they agreed to. Sign-up flow also calls acceptDpa() automatically
 * when the DPA checkbox is ticked.
 *
 * Bump DPA_VERSION whenever the customer-facing text below changes
 * materially. The acceptance audit row records the version, so historical
 * accepts remain attributable to the text in force at the time.
 */
const DPA_VERSION = '2026.05.06';

export default function Dpa() {
  const isAuthed = useIsAuthenticated();
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onAccept() {
    setBusy(true);
    setError(null);
    try {
      await acceptDpa(DPA_VERSION);
      setAccepted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <BrandedLayout>
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6 prose prose-sm">
        <h1 className="text-2xl font-semibold tracking-tight">
          Data Processing Addendum
        </h1>
        <p className="text-sm text-muted-foreground">Version {DPA_VERSION}</p>

        <h2 className="text-lg font-medium">1. Parties and Scope</h2>
        <p>
          This Data Processing Addendum (the &ldquo;<strong>DPA</strong>&rdquo;)
          supplements and forms part of the PharmaSync Terms of Service (the{' '}
          &ldquo;<strong>Agreement</strong>&rdquo;) between{' '}
          <strong>Blockchain PMO Inc.</strong> (&ldquo;<strong>Provider</strong>
          &rdquo;) and the customer identified in the relevant order form or
          account (&ldquo;<strong>Customer</strong>&rdquo;). It applies only to
          Personal Information (as defined below) processed by Provider on
          behalf of Customer in connection with the Service.
        </p>
        <p>
          Capitalized terms used but not defined here have the meaning given in
          the Agreement and/or the Privacy Policy at pharma-sync.ca/privacy.
        </p>

        <h2 className="text-lg font-medium">2. Definitions</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Personal Information</strong> &mdash; information about an
            identifiable individual, as defined under PIPEDA and Quebec Law 25.
          </li>
          <li>
            <strong>Process / Processing</strong> &mdash; any operation
            performed on Personal Information including collection, recording,
            organization, storage, use, disclosure, retention, and erasure.
          </li>
          <li>
            <strong>Service Provider</strong> (or{' '}
            <strong>Processor / Agent</strong>) &mdash; a person or entity that
            processes Personal Information on behalf of another (here,
            Provider).
          </li>
          <li>
            <strong>Controller</strong> &mdash; the person or entity that
            determines the purposes and means of processing (here, Customer).
          </li>
          <li>
            <strong>Sub-processor</strong> &mdash; a third party engaged by
            Provider that processes Personal Information on Provider&rsquo;s
            behalf in connection with the Service.
          </li>
          <li>
            <strong>Authorized User</strong> &mdash; an employee, contractor, or
            affiliate of Customer authorized by Customer to use the Service.
          </li>
          <li>
            <strong>Security Incident</strong> &mdash; a confirmed breach of
            security safeguards leading to unauthorized access, disclosure,
            alteration, loss, or destruction of Personal Information processed
            under the Agreement.
          </li>
          <li>
            <strong>Order Form</strong> &mdash; a written or electronic ordering
            document executed by both parties that specifies the Tier, fees,
            and any negotiated terms (including elections under this DPA).
          </li>
        </ul>

        <h2 className="text-lg font-medium">3. Roles of the Parties</h2>
        <p>
          <strong>3.1 Customer is the controller</strong> of Personal
          Information that Customer or its Authorized Users submit to the
          Service (for example: staff records, schedules, time-off data).
        </p>
        <p>
          <strong>3.2 Provider acts as service provider / processor</strong> on
          Customer&rsquo;s documented instructions and as set out in this DPA,
          the Agreement, and the Privacy Policy.
        </p>
        <p>
          <strong>3.3</strong> Where Provider collects Personal Information
          directly from individuals (for example, account-holder login
          credentials, billing contacts), Provider acts as the controller of
          that information, and the Privacy Policy applies.
        </p>

        <h2 className="text-lg font-medium">4. Customer Instructions</h2>
        <p>
          <strong>4.1</strong> Provider will Process Personal Information only
          on Customer&rsquo;s documented instructions, which include the
          Agreement, this DPA, and any operational settings configured by
          Customer (such as user roles, access permissions, and retention
          configuration).
        </p>
        <p>
          <strong>4.2</strong> If Provider is required by law to Process
          Personal Information in a manner inconsistent with Customer&rsquo;s
          instructions, Provider will notify Customer of that legal requirement
          before Processing, unless prohibited by law.
        </p>

        <h2 className="text-lg font-medium">5. Confidentiality and Personnel</h2>
        <p>
          <strong>5.1</strong> Provider will ensure that personnel authorized
          to Process Personal Information are bound by appropriate
          confidentiality obligations and have received training on
          Provider&rsquo;s security and privacy practices.
        </p>
        <p>
          <strong>5.2</strong> Access by Provider&rsquo;s personnel to
          Customer&rsquo;s Personal Information is limited to those who need it
          to provide the Service, on a least-privilege basis, with multi-factor
          authentication required for administrative access.
        </p>

        <h2 className="text-lg font-medium">6. Security Measures</h2>
        <p>
          Provider will implement and maintain appropriate technical and
          organizational measures designed to protect Personal Information
          against accidental or unlawful destruction, loss, alteration,
          unauthorized disclosure, or access. Such measures include, at a
          minimum:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>(a) Encryption in transit (TLS 1.2 or higher) and at rest (AES-256 or equivalent at the database layer).</li>
          <li>(b) Row-level security in the database to enforce role-based access at the data layer.</li>
          <li>(c) Logging of administrative access, with retention sufficient to support investigation.</li>
          <li>(d) Vulnerability management, dependency scanning, and timely patching.</li>
          <li>(e) Periodic backup with encryption; tested recovery procedures.</li>
          <li>(f) Network segregation and protection from common web attacks (DDoS protection, WAF).</li>
          <li>(g) Documented incident response and disaster recovery plans.</li>
          <li>(h) Background checks for personnel with access to production systems, where permitted by law.</li>
        </ul>

        <h2 className="text-lg font-medium">7. Sub-processors</h2>
        <p>
          <strong>7.1</strong> Customer authorizes Provider to engage the
          sub-processors listed in <strong>Schedule A</strong> to assist with
          the Processing of Personal Information.
        </p>
        <p>
          <strong>7.2</strong> Provider will impose contractual obligations on
          each sub-processor that are no less protective than those in this
          DPA.
        </p>
        <p>
          <strong>7.3 Notice of changes &mdash; default rule.</strong> Provider
          will provide Customer with at least <strong>30 days&rsquo; notice</strong>{' '}
          of the addition or replacement of any sub-processor through the
          in-product subprocessor list at pharma-sync.ca/subprocessors or by
          email to Customer&rsquo;s primary contact. If Customer reasonably
          objects on documented privacy or security grounds, the parties will
          work in good faith to resolve the concern; if it cannot be resolved,
          Customer may terminate the affected portion of the Service for
          convenience and receive a pro-rated refund of any prepaid unused fees
          for that portion.
        </p>
        <p>
          <strong>7.4 Enhanced notice for Pro and Enterprise tiers.</strong>{' '}
          Where the relevant Order Form specifies, the following enhanced
          sub-processor mechanic applies in place of Section 7.3 for that
          Customer:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            (a) <strong>Pro tier (or as agreed)</strong> &mdash; Provider will
            provide at least <strong>60 days&rsquo; advance written notice</strong>{' '}
            of any new or replacement sub-processor.
          </li>
          <li>
            (b) <strong>Enterprise tier (or as agreed)</strong> &mdash; Provider
            will not engage a new sub-processor without Customer&rsquo;s{' '}
            <strong>prior written consent</strong>, which will not be
            unreasonably withheld or delayed. Consent is deemed given if
            Customer does not object within 30 days of receiving notice. Where
            Customer reasonably withholds consent, the parties will work in
            good faith to identify an alternative sub-processor; if no
            alternative is available, Customer may terminate the affected
            portion of the Service for convenience and receive a pro-rated
            refund of any prepaid unused fees for that portion.
          </li>
        </ul>
        <p>
          <strong>7.5</strong> Provider remains responsible to Customer for the
          acts and omissions of its sub-processors as if they were its own.
        </p>

        <h2 className="text-lg font-medium">8. Cross-Border Transfers</h2>
        <p>
          <strong>8.1</strong> Personal Information is primarily Processed in
          Canada (see Schedule A). Some sub-processors may Process limited
          Personal Information outside of Canada, including in the United
          States, as identified in Schedule A.
        </p>
        <p>
          <strong>8.2</strong> Where Personal Information is transferred outside
          Canada, Provider will ensure that an appropriate level of protection
          is maintained through contractual safeguards (such as standard
          contractual clauses), technical measures (such as encryption in
          transit), and sub-processor due diligence. Customer acknowledges that
          information processed outside Canada may be subject to lawful access
          requests by foreign authorities and consents to this transfer for the
          purposes set out in the Privacy Policy.
        </p>
        <p>
          <strong>8.3</strong> For Customer Personal Information involving
          Quebec residents, Provider has assisted in or completed (as
          applicable) a privacy impact assessment in accordance with section 17
          of Quebec Law 25 with respect to the cross-border transfer to the
          United States via Stripe.
        </p>

        <h2 className="text-lg font-medium">9. Data Subject Requests</h2>
        <p>
          <strong>9.1</strong> Provider will, taking into account the nature of
          the Processing, provide reasonable assistance to Customer in
          fulfilling Customer&rsquo;s obligations to respond to requests from
          individuals to access, correct, withdraw consent for, port, or delete
          their Personal Information.
        </p>
        <p>
          <strong>9.2</strong> If Provider receives a request directly from a
          data subject regarding Personal Information that Provider Processes
          on Customer&rsquo;s behalf, Provider will, without undue delay and
          unless prohibited by law, refer the request to Customer.
        </p>

        <h2 className="text-lg font-medium">10. Security Incidents</h2>
        <p>
          <strong>10.1 Default notification window.</strong> Provider will
          notify Customer <strong>without unreasonable delay</strong> after
          confirming a Security Incident affecting Customer Personal
          Information, and in no event later than <strong>72 hours</strong>{' '}
          after such confirmation.
        </p>
        <p>
          <strong>10.2 Tightened window for Pro and Enterprise tiers.</strong>{' '}
          Where the relevant Order Form specifies, Provider will notify
          Customer within <strong>48 hours</strong> after confirming a Security
          Incident affecting Customer Personal Information. Customer may, by
          written notice, request a tighter window (e.g., 24 hours); Provider
          will use commercially reasonable efforts to comply but does not
          guarantee a window shorter than 48 hours absent an express agreement.
        </p>
        <p>
          <strong>10.3</strong> The notification will include, to the extent
          known: a description of the nature of the incident, the categories
          and approximate volume of Personal Information involved, the likely
          consequences, the measures taken or proposed to address it, and the
          contact point for further information.
        </p>
        <p>
          <strong>10.4</strong> The parties will cooperate in good faith to
          investigate, mitigate, and remediate Security Incidents. Notification
          under this Section 10 is not an admission of fault or liability.
        </p>

        <h2 className="text-lg font-medium">11. Audits</h2>
        <p>
          <strong>11.1 Annual summary reports.</strong> Provider will make
          available to Customer, on reasonable request and no more than{' '}
          <strong>once per calendar year</strong> (unless required by law or
          following a Security Incident), summary reports demonstrating
          compliance with this DPA. These may include third-party audit
          reports, security questionnaires, or written statements from
          Provider&rsquo;s information security personnel.
        </p>
        <p>
          <strong>11.2 Regulator-triggered third-party audit.</strong> Where
          the foregoing is insufficient and Customer is required to demonstrate
          to a regulator that further audit is necessary, the parties will
          cooperate in good faith to scope an audit by an independent
          third-party auditor, at Customer&rsquo;s cost, on terms that protect
          the confidentiality of Provider&rsquo;s environment and the data of
          other customers.
        </p>
        <p>
          <strong>11.3 Direct audit right for Enterprise tier.</strong> Where
          the relevant Order Form specifies, Customer may conduct, no more than
          once per calendar year, a direct audit of Provider&rsquo;s controls
          and systems relevant to the Processing of Customer Personal
          Information, subject to the following:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>(a) at least 30 days&rsquo; prior written notice;</li>
          <li>(b) execution of mutually acceptable confidentiality and non-disclosure undertakings;</li>
          <li>(c) audit conducted during normal business hours and in a manner that does not unreasonably disrupt Provider&rsquo;s operations;</li>
          <li>(d) audit performed by Customer&rsquo;s own personnel or a mutually acceptable third-party auditor (excluding any auditor that is a competitor of Provider);</li>
          <li>(e) at Customer&rsquo;s cost; and</li>
          <li>(f) with reasonable scope (e.g., the audit may not extend to data of other customers, source code, or trade secrets).</li>
        </ul>

        <h2 className="text-lg font-medium">12. Return or Deletion of Personal Information</h2>
        <p>
          <strong>12.1</strong> On termination of the Agreement, Customer may
          export Personal Information using in-product tools during the{' '}
          <strong>30-day grace period</strong> described in the Privacy Policy.
        </p>
        <p>
          <strong>12.2 Standard deletion timeline.</strong> After the grace
          period, Provider will delete Personal Information from production
          systems within <strong>30 days</strong>, except as required for legal
          retention (audit logs, financial records). Encrypted backups may
          persist for up to <strong>35 days</strong> after the source records
          are deleted, after which they are overwritten in the normal course of
          operations.
        </p>
        <p>
          <strong>12.3 Earlier deletion on request.</strong> Where Customer
          requests earlier deletion of Personal Information from production
          systems and/or backups, Provider will honour the request to the
          extent technically feasible and not prohibited by applicable law or
          Provider&rsquo;s other contractual obligations. Provider will confirm
          in writing the action taken and the date by which residual copies
          (e.g., in backups) will be purged.
        </p>
        <p>
          <strong>12.4</strong> Provider will, on Customer&rsquo;s written
          request, provide written confirmation of deletion.
        </p>

        <h2 className="text-lg font-medium">13. Liability</h2>
        <p>
          The liability of each party under this DPA is subject to the
          limitations of liability and exclusions in the Agreement, including
          without limitation the data-loss sub-cap set out in the Limitation of
          Liability section of the Agreement (which expressly applies to loss
          or corruption of Customer Personal Information caused by
          Provider&rsquo;s negligence or breach of its security obligations).
          Nothing in this DPA increases the scope of either party&rsquo;s
          liability beyond that provided in the Agreement.
        </p>

        <h2 className="text-lg font-medium">14. Order of Precedence</h2>
        <p>
          In case of conflict between this DPA, the Agreement, and the Privacy
          Policy, the order of precedence is: (1) this DPA; (2) the Agreement;
          (3) the Privacy Policy.
        </p>

        <h2 className="text-lg font-medium">15. Term</h2>
        <p>
          This DPA is effective for as long as Provider Processes Personal
          Information on behalf of Customer.
        </p>

        <h2 className="text-lg font-medium">16. General</h2>
        <p>
          <strong>16.1</strong> Notices to Provider under this DPA must be sent
          to <a href="mailto:privacy@pharma-sync.ca">privacy@pharma-sync.ca</a>.
        </p>
        <p>
          <strong>16.2</strong> This DPA is governed by the laws of Ontario
          (per the Agreement). The parties submit to the exclusive jurisdiction
          of the courts of Ontario.
        </p>
        <p>
          <strong>16.3</strong>{' '}
          <em>
            Les parties ont exigé que le présent addenda soit rédigé en
            anglais.
          </em>
        </p>

        <h2 className="text-lg font-medium">Schedule A &mdash; Authorized Sub-processors</h2>
        <div className="not-prose overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-2 pr-4 font-medium">Sub-processor</th>
                <th className="py-2 pr-4 font-medium">Service provided</th>
                <th className="py-2 font-medium">Region(s)</th>
              </tr>
            </thead>
            <tbody className="align-top">
              <tr className="border-b border-border/60">
                <td className="py-2 pr-4">Supabase Inc.</td>
                <td className="py-2 pr-4">Database hosting, authentication, file storage</td>
                <td className="py-2">Canada (ca-central-1, Montreal)</td>
              </tr>
              <tr className="border-b border-border/60">
                <td className="py-2 pr-4">Vercel Inc.</td>
                <td className="py-2 pr-4">Web application hosting (CDN, serverless)</td>
                <td className="py-2">Global edge network; primary compute in Canada/US</td>
              </tr>
              <tr className="border-b border-border/60">
                <td className="py-2 pr-4">Cloudflare Inc.</td>
                <td className="py-2 pr-4">DDoS protection, traffic routing, edge caching</td>
                <td className="py-2">Global edge network</td>
              </tr>
              <tr className="border-b border-border/60">
                <td className="py-2 pr-4">Google LLC (Google Workspace)</td>
                <td className="py-2 pr-4">Operational email and productivity tools</td>
                <td className="py-2">Multi-region</td>
              </tr>
              <tr className="border-b border-border/60">
                <td className="py-2 pr-4">GoDaddy Operating Co., LLC</td>
                <td className="py-2 pr-4">Domain registration and DNS</td>
                <td className="py-2">DNS records only &mdash; no Personal Information processed</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Stripe Payments Canada Ltd. / Stripe Inc.</td>
                <td className="py-2 pr-4">Payment processing</td>
                <td className="py-2">
                  <strong>Cross-border (United States)</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          The current authoritative list is published at{' '}
          <strong>pharma-sync.ca/subprocessors</strong>.
        </p>

        <h2 className="text-lg font-medium">Schedule B &mdash; Description of Processing</h2>
        <p>
          <strong>Subject matter and duration.</strong> Processing of Personal
          Information by Provider on behalf of Customer for the duration of the
          Agreement and up to the deletion timelines set out in Section 12.
        </p>
        <p>
          <strong>Nature and purpose.</strong> To provide the PharmaSync staff
          scheduling and time-off management Service in accordance with the
          Agreement.
        </p>
        <p>
          <strong>Types of Personal Information.</strong>
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Identification: name, email, phone, role, language preference.</li>
          <li>Employment: hire date, role, location assignment, hourly rate (Pro tier and above), expected hours per week, employment status.</li>
          <li>Operational: schedules, shifts, time-off requests, approval/denial reasons, schedule notes.</li>
          <li>Authentication and audit: hashed credentials, MFA tokens, audit log entries.</li>
        </ul>
        <p>
          <strong>Categories of data subjects.</strong>
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Customer&rsquo;s pharmacy owners, managers, pharmacists, and staff.</li>
          <li>Customer&rsquo;s billing and administrative contacts.</li>
        </ul>
        <p>
          <strong>Sensitivity.</strong> Some Personal Information may be
          considered sensitive (e.g., individual hourly compensation).
          Sensitive information is access-controlled both by application logic
          (role-based access) and by data-layer rules (row-level security).
        </p>

        {isAuthed && (
          <div className="rounded-lg border border-border bg-card p-6 space-y-3 not-prose">
            {accepted ? (
              <p className="text-sm text-green-700">
                ✓ DPA acceptance recorded for version {DPA_VERSION}.
              </p>
            ) : (
              <>
                <p className="text-sm">
                  By clicking accept, you agree to this Data Processing
                  Agreement on behalf of your organization. We log this
                  acceptance with your account and the version above.
                </p>
                {error && (
                  <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm text-destructive">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={onAccept}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? 'Recording…' : 'I accept the DPA'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </BrandedLayout>
  );
}
            